"use client";

import React, { useEffect } from "react";
import Link from "next/link";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import {
  ShoppingCart,
  ChevronRight,
  MessageSquare,
  Star,
  Truck,
  ShieldCheck,
  Factory,
  CreditCard,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { IHomePageData } from "./homeModel";
import { createLookupObject } from "@/helper/helper";
import { useAppSelector } from "@/redux/hooks";
import { ISetting } from "../setting/settingModel";
import { FaClock, FaWhatsapp } from "react-icons/fa";
import { OrderGuideSection } from "@/components/OrderGuideSection";
import { ArrowDown } from "lucide-react";
import Image from "next/image";
import { getEffectivePrice } from "@/utils/priceUtils";

interface HomeClientProps {
  homeData: IHomePageData | null;
}

const HomeClient: React.FC<HomeClientProps> = ({ homeData }) => {
  const { isCustomerAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const { isInternational } = useSelector(
    (state: RootState) => state.location,
  );
  const [CMSData, setCMSData] = React.useState<any>({});
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const Settings: Record<string, ISetting> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISetting>,
  );

  const WhatsAppNo = Settings?.["WhatsAppNo"]?.SettingValue;
  const WhatsAppNo2 = Settings?.["WhatsAppNo2"]?.SettingValue;
  const CallTiming = Settings?.["CallTiming"]?.SettingValue;
  const phone = WhatsAppNo ? WhatsAppNo.replace(/\D+/g, "") : "";
  const whatsappLink = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent("Hello, I'd like to know more details.")}`
    : "#";
  const phone2 = WhatsAppNo2 ? WhatsAppNo2.replace(/\D+/g, "") : "";
  const whatsappLink2 = phone2
    ? `https://wa.me/${phone2}?text=${encodeURIComponent("Hello, I'd like to know more details.")}`
    : "#";
  const showLoginDiscountMsg =
    !isCustomerAuthenticated || (isCustomerAuthenticated && user?.isFirstOrder);

  const YouTubeVideoLink = Settings?.["YouTubeVideoLink"]?.SettingValue;
  const YouTubeVideoLink2 = Settings?.["YouTubeVideoLink2"]?.SettingValue;

  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return "";
    let videoId = "";
    if (url.includes("v=")) {
      videoId = url.split("v=")[1].split("&")[0];
    } else if (url.includes("youtu.be/")) {
      videoId = url.split("youtu.be/")[1].split("?")[0];
    } else if (url.includes("embed/")) {
      videoId = url.split("embed/")[1].split("?")[0];
    } else if (url.includes("shorts/")) {
      videoId = url.split("shorts/")[1].split("?")[0];
    }
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  useEffect(() => {
    if (homeData?.CMS && homeData?.CMS?.length > 0) {
      setCMSData(createLookupObject(homeData?.CMS, "Name"));
    }
  }, [homeData?.CMS]);

  // Group FeaturedProducts by Category
  const productsByCategory = (homeData?.FeaturedProducts || []).reduce(
    (acc: any, product: any) => {
      const catName = product.CategoryName || "Others";
      if (!acc[catName]) {
        acc[catName] = {
          categoryName: catName,
          categoryID: product.CategoryID,
          products: [],
        };
      }
      if (acc[catName].products.length < 2) {
        acc[catName].products.push(product);
      }
      return acc;
    },
    {},
  );

  const categoryEntries: any[] = Object.values(productsByCategory);

  const { ActiveDiscountList } = useSelector(
    (state: RootState) => state.discount,
  );

  const displaySlabs =
    ActiveDiscountList.length > 0
      ? ActiveDiscountList.map((s) => ({
          range: `₹${s.FromValue.toLocaleString()} – ₹${s.ToValue.toLocaleString()}`,
          pct: `${s.DiscountPercent}%`,
          login: "5%",
          order: "0%",
        }))
      : [];

  const orderGuideRef = React.useRef<HTMLDivElement>(null);

  const scrollToGuide = () => {
    orderGuideRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="min-h-screen">
      {/* TOP BANNER */}
      {showLoginDiscountMsg && (
        <div className="bg-gradient-to-r from-black-2 via-gold-pale to-black-2 border-b border-border-1 text-center py-2.5 px-5 text-xs font-normal tracking-wide text-text-1">
          🔐{" "}
          <span className="bg-gradient-to-r from-gold-dim to-gold-core text-white px-3.5 py-0.5 rounded-full font-bold text-[11px] tracking-widest mx-2 shadow-sm">
            5% EXTRA
          </span>
          discount unlocked when you{" "}
          <Link
            href="/login"
            className="text-gold-core font-bold underline decoration-gold-dim underline-offset-4"
          >
            login
          </Link>{" "}
          &nbsp;·&nbsp; Minimum order ₹3,000
          {!isCustomerAuthenticated && (
            <Link
              href="/account"
              className="text-gold-core font-medium ml-2.5 underline decoration-gold-dim underline-offset-4 decoration-1"
            >
              Register Free →
            </Link>
          )}
        </div>
      )}

      <Navbar />

      {/* HERO SECTION */}
      {CMSData?.["Home - Section 1 : Banner"] ? (
        <section
          className="relative min-h-[95vh] flex items-center justify-center overflow-hidden py-20 px-4 md:px-14 text-center"
          style={{
            background:
              "linear-gradient(145deg, #0d0202 0%, #2a0a0a 35%, #5c2020 65%, #7a2a2a 100%)",
          }}
          dangerouslySetInnerHTML={{
            __html: CMSData?.["Home - Section 1 : Banner"]?.Template,
          }}
        />
      ) : (
        <section
          className="relative min-h-[95vh] flex items-center justify-center overflow-hidden py-20 px-4 md:px-14 text-center"
          style={{
            background:
              "linear-gradient(145deg, #0d0202 0%, #2a0a0a 35%, #5c2020 65%, #7a2a2a 100%)",
          }}
        >
          {/* Atmospheric layers */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Upper-left warm glow */}
            <div
              className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full blur-[120px]"
              style={{ background: "rgba(197,160,89,0.15)" }}
            />
            {/* Bottom-right maroon depth */}
            <div
              className="absolute -bottom-20 -right-20 w-[400px] h-[400px] rounded-full blur-[100px]"
              style={{ background: "rgba(62,21,21,0.5)" }}
            />
            {/* Diagonal shimmer lines */}
            <div className="absolute left-[25%] top-[-40%] w-[1.5px] h-[180%] bg-gradient-to-b from-transparent via-amber-200 to-transparent -rotate-[25deg] opacity-20" />
            <div className="absolute right-[25%] top-[-40%] w-[1.5px] h-[180%] bg-gradient-to-b from-transparent via-amber-200 to-transparent rotate-[25deg] opacity-20" />
            {/* Subtle dot grid */}
            <div
              className="absolute inset-0 opacity-[0.04]"
              style={{
                backgroundImage:
                  "radial-gradient(circle, rgba(245,239,229,1) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
          </div>

          <div className="relative z-10 max-w-4xl space-y-7 animate-in fade-in slide-in-from-bottom-6 duration-1000">
            <div
              className="inline-flex items-center gap-3.5 text-[11px] font-medium tracking-[0.28em] uppercase before:h-px before:w-10 before:bg-gradient-to-r before:from-transparent before:to-amber-300 after:h-px after:w-10 after:bg-gradient-to-l after:from-transparent after:to-amber-300"
              style={{ color: "#c5a059" }}
            >
              Wholesale Imitation & Artificial Jewellery — Ahmedabad, Gujarat
            </div>

            <h1 className="font-lobster text-5xl md:text-8xl font-light leading-none text-white tracking-tight">
              India's #1{" "}
              <span
                className="italic font-semibold gold-bg-gradient pr-2"
                style={{
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Imitation
              </span>{" "}
              &<br />
              <span
                className="italic font-semibold gold-bg-gradient pr-3"
                style={{
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Artificial
              </span>{" "}
              Jewellery
              <br />
              <span className="text-[0.63em] font-light text-amber-200/70">
                Wholesale Supplier
              </span>
            </h1>

            <p className="text-sm md:text-base text-amber-100/70 leading-relaxed max-w-lg mx-auto font-light tracking-wide">
              We are a{" "}
              <strong className="text-white font-medium">wholesale-only</strong>{" "}
              manufacturer and supplier of premium imitation & artificial
              jewellery — Silver, Gold, AD & Bridal.{" "}
              <strong className="text-white font-medium">
                Bulk orders only.
              </strong>{" "}
              Trusted by Wholesalers, Flipkart, Amazon, Meesho, Myntra sellers &
              retailers across India.
            </p>

            <div className="flex flex-wrap justify-center gap-4 py-8">
              <Link
                href="/shop"
                className="bg-black text-gold-core px-10 py-3.5 text-xs font-bold uppercase tracking-[0.14em] transition-all hover:bg-black-2 hover:shadow-[0_10px_30px_rgba(0,0,0,0.2)] hover:-translate-y-0.5"
              >
                Shop
              </Link>
              <Link
                href="/wholesale"
                className="border border-black/30 text-white px-10 py-3.5 text-xs font-normal uppercase tracking-[0.14em] transition-all hover:border-black/60 hover:bg-white/10"
              >
                Bulk / Wholesale Inquiry
              </Link>
            </div>

            <div
              className="inline-grid grid-cols-2 md:grid-cols-4 overflow-hidden"
              style={{
                border: "1px solid rgba(255,255,255,0.15)",
                background: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(12px)",
              }}
            >
              {[
                { label: "Products", value: "500+" },
                { label: "Min. Order", value: "₹3,000" },
                { label: "Max Discount", value: "15%" },
                { label: "Shipping", value: "PAN India" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="px-9 py-4.5 text-center"
                  style={{
                    borderRight:
                      i < 3 ? "1px solid rgba(255,255,255,0.12)" : "none",
                  }}
                >
                  <div className="font-cormorant text-3xl font-bold text-white leading-none">
                    {stat.value}
                  </div>
                  <div className="text-[10px] text-amber-200/50 uppercase tracking-widest mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TRUST BAR */}
      {CMSData?.["Home - Section 2 : Shipping Detail"] && (
        <div
          className="bg-black-3 border-y border-border-1 py-5.5 px-4 md:px-14 flex flex-wrap items-center justify-center gap-6 md:gap-11 text-xs text-text-2"
          dangerouslySetInnerHTML={{
            __html: CMSData?.["Home - Section 2 : Shipping Detail"]?.Template,
          }}
        />
      )}
      {/* <div className="bg-black-3 border-y border-border-1 py-5.5 px-4 md:px-14 flex flex-wrap items-center justify-center gap-6 md:gap-11 text-xs text-text-2">
        <div className="flex items-center gap-2.5">
          📦 Dispatched in{" "}
          <strong className="text-text-1 font-medium">1–3 Business Days</strong>
        </div>
        <div className="hidden md:block w-px h-4.5 bg-border-1" />
        <div className="flex items-center gap-2.5">
          🚚{" "}
          <strong className="text-text-1 font-medium">3–10 Day Delivery</strong>{" "}
          PAN India
        </div>
        <div className="hidden md:block w-px h-4.5 bg-border-1" />
        <div className="flex items-center gap-2.5">
          📍 <strong className="text-text-1 font-medium">Full Tracking</strong>{" "}
          on all orders
        </div>
        <div className="hidden md:block w-px h-4.5 bg-border-1" />
        <div className="flex items-center gap-2.5">
          💳 <strong className="text-text-1 font-medium">UPI / Card</strong> —
          No COD
        </div>
        <div className="hidden md:block w-px h-4.5 bg-border-1" />
        <div className="flex items-center gap-2.5 uppercase font-bold text-gold-core">
          ⭐ Verified on IndiaMart
        </div>
      </div> */}

      {/* 2-COLUMN SIGNATURE COLLECTION SECTION */}
      <section className="py-24 px-4 md:px-14 bg-black overflow-hidden border-y border-gold-core/10">
        <div className="flex flex-col md:flex-row justify-between items-baseline gap-6 mb-16">
          <div className="space-y-3">
            <div className="text-gold-core text-[11px] font-bold tracking-[0.4em] uppercase flex items-center gap-4">
              <span className="h-px w-10 bg-gold-core" />
              Signature Collections
            </div>
            <h2 className="font-cormorant text-5xl md:text-7xl font-normal text-text-1 leading-tight">
              Our <em className="italic gold-text-gradient">Best value</em>{" "}
              Designs
            </h2>
          </div>
          <div className="flex flex-col items-end text-right">
            <p className="text-[12px] text-text-3 font-light max-w-sm tracking-widest leading-relaxed mb-6 uppercase decoration-gold-dim/20 underline underline-offset-8">
              Lowest factory-direct wholesale rates.{" "}
              {showLoginDiscountMsg
                ? "Member discounts applied below."
                : "Exclusive member rates active."}
            </p>
            <Link
              href="/shop"
              className="group/all flex items-center gap-3 text-[10px] text-gold-core font-bold uppercase tracking-[0.3em] transition-all hover:gap-5"
            >
              Explore All Categories{" "}
              <ChevronRight
                size={14}
                className="group-hover/all:translate-x-1 transition-transform"
              />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border-1 border border-border-1 shadow-md">
          {categoryEntries?.map((cat: any, i: number) => (
            <div
              key={i}
              className="group p-6 md:p-10 bg-black transition-all duration-700 hover:bg-black-2 flex flex-col items-center text-center"
            >
              <div className="w-full space-y-2 mb-12">
                <h3 className="font-cormorant text-3xl md:text-5xl font-bold gold-text-gradient italic leading-none truncate px-4">
                  {cat.categoryName}
                </h3>
                <div className="h-0.5 w-20 bg-gold-core mx-auto opacity-40 group-hover:w-32 transition-all duration-700" />
              </div>

              <div
                className={`w-full grid ${
                  cat.products.length === 1
                    ? "grid-cols-1 max-w-sm mx-auto"
                    : "grid-cols-1 sm:grid-cols-2"
                } gap-10 md:gap-14`}
              >
                {cat.products.map((prod: any, j: number) => {
                  const effectiveBasePrice = getEffectivePrice(
                    prod.Price,
                    isInternational,
                  );
                  const finalPrice = showLoginDiscountMsg
                    ? effectiveBasePrice * 0.95
                    : effectiveBasePrice;
                  return (
                    <Link
                      key={j}
                      href={`/product/${prod.URL}`}
                      className="group/item flex flex-col items-center"
                    >
                      <div className="w-full aspect-[4/5] bg-black-2 relative overflow-hidden border border-border-1 transition-all duration-700 group-hover/item:border-gold-dim/50 group-hover/item:shadow-[0_20px_40px_rgba(0,0,0,0.3)] group-hover/item:-translate-y-1">
                        {prod.MainImage ? (
                          <Image
                            fill
                            src={prod.MainImage}
                            alt={prod.ProductName}
                            className="w-full h-full object-cover opacity-90 group-hover/item:opacity-100 group-hover/item:scale-105 transition-all duration-[1.5s]"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-5xl grayscale opacity-[0.1]">
                            💍
                          </div>
                        )}
                        {/* <div className="absolute top-4 left-4 bg-black/80 backdrop-blur-md px-3 py-1 border border-gold-dim/30 text-[9px] text-gold-core font-bold tracking-widest uppercase">
                            WHOLESALE
                          </div> */}
                      </div>

                      <div className="mt-8 space-y-2 w-full text-center">
                        <h4 className="text-[12px] font-bold text-text-1 tracking-[0.2em] group-hover/item:text-gold-core transition-colors line-clamp-2 px-2 uppercase">
                          {prod.ProductName}
                        </h4>
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-[9px] text-text-3 font-medium uppercase tracking-[0.1em] opacity-60">
                            Starts at
                          </span>
                          <div className="font-cormorant text-4xl md:text-5xl font-extrabold gold-text-gradient leading-none">
                            ₹
                            {Math.round(finalPrice).toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </div>
                          {showLoginDiscountMsg && (
                            <div className="text-[10px] text-gold-dim font-bold mt-1.5 animate-pulse">
                              -5% MEMBERS ONLY
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <Link
                href={`/shop?category=${cat.categoryID}`}
                className="text-[10px] text-text-3 hover:text-gold-core uppercase font-bold tracking-[0.15em] transition-colors flex items-center gap-2 group/btn mt-5"
              >
                Show All {cat.categoryName}{" "}
                <ChevronRight
                  size={12}
                  className="group-hover/btn:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* MANUFACTURER BANNER */}
      {CMSData?.["Home - Section 4 : Wholesale Jewellery Manufacturer"] && (
        <section
          className="text-off-white bg-mfr-glow border-y border-border-gold py-16 px-4 md:px-14 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(145deg, #0d0202 0%, #2a0a0a 35%, #5c2020 65%, #7a2a2a 100%)",
          }}
          dangerouslySetInnerHTML={{
            __html:
              CMSData?.["Home - Section 4 : Wholesale Jewellery Manufacturer"]
                ?.Template,
          }}
        />
      )}
      {/* <section
        className="text-off-white bg-mfr-glow border-y border-border-gold py-16 px-4 md:px-14 relative overflow-hidden"
        style={{
          background:
            "linear-gradient(145deg, #0d0202 0%, #2a0a0a 35%, #5c2020 65%, #7a2a2a 100%)",
        }}
      >
        <div className="absolute left-1/2 top-[-10%] w-[2px] h-[120%] bg-gradient-to-b from-transparent via-gold-glow to-transparent opacity-30 pointer-events-none" />
        <div className="relative z-10 flex flex-col lg:flex-row items-start justify-between gap-12">
          <div className="flex-[1.5] space-y-6">
            <div className="inline-flex items-center gap-2 border border-gold-dim bg-gold-core text-white text-[9px] font-bold tracking-[0.22em] uppercase px-4.5 py-1.5 rounded-sm">
              ✦ Wholesale Jewellery Manufacturer
            </div>
            <h2 className="font-cormorant text-4xl md:text-5xl font-semibold  leading-tight">
              We Give You the{" "}
              <em className="italic accent-text-gradient">Best Price</em>
              <br />
              Because We Manufacture
              <br />& Sell{" "}
              <em className="italic accent-text-gradient">Directly</em> to You
            </h2>
            <p className="text-sm leading-relaxed font-light max-w-lg">
              We are a <strong>wholesale-only</strong> manufacturer of imitation
              and artificial jewellery. Bulk orders directly from our factory in
              Ahmedabad with zero middleman markup.{" "}
              <strong>We do not sell individual pieces</strong> — minimum order
              ₹3,000.
            </p>
            <a
              href="https://www.indiamart.com/malabarenterprise/"
              target="_blank"
              className="btn-white inline-block"
            >
              ⭐ Check Our Rating on IndiaMart
            </a>
          </div>

          <div className="flex-1 w-full space-y-2.5">
            {[
              {
                icon: <Factory size={16} />,
                text: "In-house manufacturing — Ahmedabad, Gujarat",
              },
              {
                icon: <ShieldCheck size={16} />,
                text: "Trusted wholesale partner for 1000+ retailers",
              },
              {
                icon: <ShoppingCart size={16} />,
                text: "Flipkart · Amazon · Meesho · Myntra sellers welcome",
              },
              {
                icon: <Truck size={16} />,
                text: "All orders shipped with full tracking",
              },
              {
                icon: <Star size={16} />,
                text: "Verified profile & reviews on IndiaMart",
              },
            ].map((feat, i) => (
              <div
                key={i}
                className="flex items-center gap-4 bg-white/[0.02] border border-border-1 border-l-2 border-l-ivory p-3.5 hover:bg-gold-dim/5 hover:border-l-white transition-all"
              >
                <span className="text-off-white">{feat.icon}</span>
                <span className="text-xs font-light">{feat.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* VIDEO GUIDES SECTION */}
      {isInternational && (YouTubeVideoLink || YouTubeVideoLink2) && (
        <section className="bg-black-2 py-24 px-4 md:px-14 border-y border-gold-pale/10 relative overflow-hidden">
          {/* Decorative Background Elements */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold-core/5 rounded-full blur-[120px] -mr-64 -mt-64 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gold-core/5 rounded-full blur-[120px] -ml-64 -mb-64 pointer-events-none" />

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center space-y-4 mb-20 max-w-2xl mx-auto">
              <div className="text-gold-core text-[11px] font-bold tracking-[0.4em] uppercase flex items-center justify-center gap-4">
                <span className="h-px w-10 bg-gold-core" />
                Customer Guides
                <span className="h-px w-10 bg-gold-core" />
              </div>
              <h2 className="font-cormorant text-4xl md:text-6xl text-text-1 font-medium">
                Step-by-Step{" "}
                <em className="italic gold-text-gradient">Video Tutorials</em>
              </h2>
              <p className="text-text-3 font-light leading-relaxed tracking-wide">
                We've prepared these short guides to help you navigate our
                wholesale platform seamlessly. Learn everything from basic
                access to account recovery.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
              {YouTubeVideoLink && (
                <div className="space-y-6 group animate-in fade-in slide-in-from-left-6 duration-1000">
                  <div className="flex items-center gap-5 border-b border-border-1 pb-5">
                    <span className="w-10 h-10 rounded-full border border-gold-dim bg-gold-core/10 text-gold-core flex items-center justify-center text-xs font-bold font-jost">
                      01
                    </span>
                    <h3 className="text-xl font-cormorant text-text-1 uppercase tracking-[0.2em] group-hover:text-gold-core transition-colors font-bold">
                      How to Login
                    </h3>
                  </div>
                  <div className="group/video aspect-video bg-black-4 border border-border-1 overflow-hidden shadow-2xl group-hover:border-gold-core/50 transition-all duration-500 hover:shadow-gold-core/10 relative">
                    <iframe
                      src={getYouTubeEmbedUrl(YouTubeVideoLink)}
                      className="w-full h-full relative z-10"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="How to login to our website"
                    ></iframe>
                    <div className="absolute inset-x-0 -bottom-1 h-1 bg-gold-core scale-x-0 group-hover/video:scale-x-100 transition-transform duration-700 origin-left" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-gold-dim/20 to-transparent" />
                    <p className="text-[10px] text-text-3 uppercase tracking-widest font-bold opacity-60">
                      Tutorial: Secure Access
                    </p>
                  </div>
                </div>
              )}

              {YouTubeVideoLink2 && (
                <div className="space-y-6 group animate-in fade-in slide-in-from-right-6 duration-1000">
                  <div className="flex items-center gap-5 border-b border-border-1 pb-5">
                    <span className="w-10 h-10 rounded-full border border-gold-dim bg-gold-core/10 text-gold-core flex items-center justify-center text-xs font-bold font-jost">
                      02
                    </span>
                    <h3 className="text-xl font-cormorant text-text-1 uppercase tracking-[0.2em] group-hover:text-gold-core transition-colors font-bold">
                      Reset Password
                    </h3>
                  </div>
                  <div className="group/video aspect-video bg-black-4 border border-border-1 overflow-hidden shadow-2xl group-hover:border-gold-core/50 transition-all duration-500 hover:shadow-gold-core/10 relative">
                    <iframe
                      src={getYouTubeEmbedUrl(YouTubeVideoLink2)}
                      className="w-full h-full relative z-10"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="How to reset password"
                    ></iframe>
                    <div className="absolute inset-x-0 -bottom-1 h-1 bg-gold-core scale-x-0 group-hover/video:scale-x-100 transition-transform duration-700 origin-left" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-px flex-1 bg-gradient-to-r from-gold-dim/20 to-transparent" />
                    <p className="text-[10px] text-text-3 uppercase tracking-widest font-bold opacity-60">
                      Tutorial: Account Recovery
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* DECORATIVE DIVIDER */}
      <div className="bg-white py-4 flex items-center justify-center gap-6">
        <div className="h-px w-20 bg-gradient-to-r from-transparent to-gold-core" />
        <span className="text-gold-core text-[10px] tracking-[0.5em]">✦</span>
        <div className="h-px w-20 bg-gradient-to-l from-transparent to-gold-core" />
      </div>

      {/* HOW TO ORDER TEASER */}
      <section className="bg-white py-16 px-4 md:px-14 border-b border-border-1">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-12">
          <div className="flex-1 space-y-6">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-gold-core flex items-center gap-3">
              Simple Process
              <span className="h-px w-10 bg-gold-dim" />
            </div>
            <h2 className="font-cormorant text-4xl md:text-5xl text-text-1 leading-tight">
              3 Simple Steps to <br />
              <em className="italic gold-text-gradient">Start Wholesaling</em>
            </h2>
            <p className="text-text-3 font-light leading-relaxed max-w-md">
              We've streamlined our ordering process to ensure fast, secure, and
              reliable service for wholesale partners across India.
            </p>
            <button
              onClick={scrollToGuide}
              className="group flex items-center gap-3  text-gold-core font-bold uppercase tracking-[0.2em] hover:text-text-1 transition-colors cursor-pointer"
            >
              View Full Ordering Guide
              <ArrowDown
                size={14}
                className="group-hover:translate-y-1 transition-transform"
              />
            </button>
          </div>

          <div className="flex-[1.5] w-full grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                text: "Add Items",
                desc: "Min. Order ₹3,000",
                icon: <ShoppingCart size={20} />,
              },
              {
                step: "02",
                text: "Pay & Proof",
                desc: "UPI / Bank Transfer",
                icon: <CreditCard size={20} />,
              },
              {
                step: "03",
                text: "Fast Delivery",
                desc: "PAN India Shipping",
                icon: <Truck size={20} />,
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-black-2 border border-border-1 p-8 space-y-5 group hover:border-gold-dim/40 transition-all duration-500 relative overflow-hidden"
              >
                <div className="absolute -right-2 -bottom-2 text-6xl font-cormorant italic text-black/[0.03] pointer-events-none group-hover:text-gold-dim/5 transition-colors">
                  {item.step}
                </div>
                <div className="w-12 h-12 bg-black border border-border-1 flex items-center justify-center text-gold-core group-hover:border-gold-dim/30 transition-colors">
                  {item.icon}
                </div>
                <div className="space-y-1.5 relative z-10">
                  <h4 className="text-[13px] text-text-1 font-bold uppercase tracking-widest">
                    {item.text}
                  </h4>
                  <p className="text-[12px] text-text-3 font-light">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISCOUNT SLABS */}
      {displaySlabs && displaySlabs?.length > 0 ? (
        <section className="py-20 px-4 md:px-14 bg-black border-b border-border-1">
          <div className="text-gold-core text-[10px] font-semibold tracking-[0.28em] uppercase flex items-center gap-3.5 mb-4">
            Wholesale Pricing
            <span className="h-px w-20 bg-gradient-to-r from-gold-dim to-transparent" />
          </div>
          <h2 className="font-cormorant text-4xl md:text-6xl font-normal text-text-1 leading-tight mb-3">
            Order More, <em className="italic gold-text-gradient">Save More</em>
          </h2>
          <p className="text-text-2 leading-relaxed font-light max-w-md mb-14">
            Tiered wholesale discounts applied automatically at checkout.{" "}
            {showLoginDiscountMsg &&
              "Login free to unlock your additional 5% on orders over ₹3,000."}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0.5 bg-border-1 border border-border-1 shadow-2xl overflow-hidden">
            {displaySlabs?.slice(0, 4)?.map((slab, i) => {
              const isBest = i === 3 || i === displaySlabs.length - 1;
              return (
                <div
                  key={i}
                  className={`relative overflow-hidden p-12 group transition-all duration-700 ${isBest ? "bg-black" : "bg-white"} hover:bg-black-3`}
                >
                  <div className="absolute -right-8 -bottom-8 font-cormorant italic text-[140px] font-bold text-gold-core/5 select-none pointer-events-none group-hover:text-gold-core/10 transition-colors">
                    0{i + 1}
                  </div>
                  {isBest && (
                    <div className="absolute top-4 -right-6 bg-gold-core text-black text-[8px] font-extrabold tracking-widest px-8 py-1 rotate-[35deg]">
                      BEST VALUE
                    </div>
                  )}
                  <div className="text-[10px] text-gold-core font-bold uppercase tracking-[0.3em] mb-6">
                    Tier {i + 1}
                  </div>
                  <div className="text-[10px] text-text-3 uppercase tracking-widest mb-4">
                    {slab.range}
                  </div>
                  <div className="font-cormorant text-8xl font-normal leading-none gold-text-gradient mb-4">
                    {slab.pct}
                  </div>
                  <div className="h-px w-10 bg-gold-dim group-hover:w-20 transition-all duration-700 mb-6" />
                  <div className="flex justify-between py-1 text-[11px] tracking-wide">
                    <span className="text-text-3 uppercase">
                      Member Benefit
                    </span>
                    <span className="text-gold-core font-bold">
                      {slab.login}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 border border-border-1 bg-gold-core/[0.04] p-5 flex items-center gap-4 shadow-sm">
            <span className="text-xl">🔐</span>
            <p className="text-[13px] text-text-2 leading-relaxed">
              {showLoginDiscountMsg ? (
                <>
                  <strong className="gold-text-gradient font-bold">
                    Login to unlock 5% extra discount on orders over ₹3,000.
                  </strong>{" "}
                  All prices shown on this website are already potential member
                  prices. Register free and start saving today.
                </>
              ) : (
                <>
                  <strong className="gold-text-gradient font-bold">
                    Member Exclusive Pricing
                  </strong>{" "}
                  Your factory-direct wholesale rates are active. Bulk order
                  discounts will be calculated at checkout.
                </>
              )}
            </p>
          </div>
        </section>
      ) : null}

      {/* INDIAMART TRUST SECTION */}
      {CMSData?.["Home - Section 3 : Rating & Reviews"] && (
        <section
          className="bg-white border-y border-gold-pale py-15 px-4 md:px-14"
          dangerouslySetInnerHTML={{
            __html: CMSData?.["Home - Section 3 : Rating & Reviews"]?.Template,
          }}
        />
      )}
      {/* <section className="bg-trust-gradient border-y border-gold-pale py-15 px-4 md:px-14">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-10 md:gap-20">
          <div className="flex-1 space-y-6">
            <div className="text-[10px] font-bold tracking-[0.26em] uppercase text-gold-core flex items-center gap-3">
              Why Trust Us
              <span className="h-px w-15 bg-gradient-to-r from-gold-dim to-transparent" />
            </div>
            <h2 className="font-cormorant text-3xl md:text-5xl font-normal text-text-1 leading-tight">
              Check Our Rating & Reviews
              <br />
              on <em className="italic gold-text-gradient">IndiaMart</em>
            </h2>
            <p className=" text-text-2 leading-relaxed font-light max-w-md">
              We are a verified wholesale supplier on IndiaMart with genuine
              customer ratings and reviews. Before you place your first bulk
              order — visit our profile and see what hundreds of retailers and
              resellers say about us.
            </p>
            <div className="flex flex-wrap items-center gap-4 pb-6">
              <a
                href="https://www.indiamart.com/malabarenterprise/"
                target="_blank"
                className="btn-gold flex items-center gap-2"
              >
                ⭐ View Our IndiaMart Profile & Reviews
              </a>
            </div>
          </div>

          <div className="flex flex-col gap-3.5 w-full lg:w-auto">
            {[
              {
                icon: "⭐",
                title: "Verified Supplier",
                sub: "IndiaMart Trusted Profile",
              },
              {
                icon: "🏭",
                title: "1000+ Retailers",
                sub: "Trust NakMan Jewellery",
              },
              {
                icon: "📦",
                title: "Bulk Orders Only",
                sub: "Wholesale · Min. ₹3,000",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="bg-gold-dim/10 border border-gold-pale p-5 flex items-center gap-4 hover:bg-gold-dim/20 transition-colors group"
              >
                <span className="text-3xl filter drop-shadow-[0_0_8px_rgba(200,150,26,0.5)] group-hover:scale-110 transition-transform">
                  {card.icon}
                </span>
                <div>
                  <div className="font-cormorant text-2xl font-bold gold-text-gradient leading-none">
                    {card.title}
                  </div>
                  <div className="text-[11px] text-text-3 tracking-wide mt-1 uppercase">
                    {card.sub}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* FULL ORDER GUIDE */}
      <section ref={orderGuideRef} className="bg-black py-10 px-4 md:px-14">
        <div className="max-w-7xl mx-auto">
          <OrderGuideSection />
        </div>
      </section>

      {/* SUPPORT SECTION */}
      <section className="bg-black-3 bg-support-glow border-y border-border-1 py-16 px-4 md:px-14 flex flex-col lg:flex-row items-start justify-between gap-12">
        <div className="flex-[1.2] space-y-7">
          <h2 className="font-cormorant text-4xl md:text-5xl font-normal text-text-1">
            We're Here to Help
          </h2>
          <p className="text-text-2 leading-relaxed font-light max-w-sm">
            Our support team is available every day. Chat with us on WhatsApp
            for fast assistance with orders, products, and shipping.
          </p>
          <div className="inline-flex items-center gap-2.5 border border-border-1 bg-black px-5 py-2.5 text-xs text-gold-core tracking-wide shadow-sm">
            <FaClock className="min-w-[12px]" /> {CallTiming}
          </div>
          <div className="flex flex-wrap gap-3">
            {WhatsAppNo && (
              <a
                href={whatsappLink}
                target="_blank"
                className="flex items-center gap-3 bg-green-500 p-3.5 px-5 hover:bg-green-600 transition-colors group rounded-md"
              >
                <FaWhatsapp className="text-2xl text-text-1 group-hover:scale-110 transition-transform" />
                <div>
                  <div className="text-[10px] text-text-1/60 tracking-widest leading-none mb-1 text-left">
                    WhatsApp Support
                  </div>
                  <div className="text-sm font-semibold text-text-1">
                    {WhatsAppNo}
                  </div>
                </div>
              </a>
            )}
            {WhatsAppNo2 && (
              <a
                href={whatsappLink2}
                target="_blank"
                className="flex items-center gap-3 bg-[#0B4D3E] border border-[#1A6B56] p-3.5 px-5 hover:bg-[#0d5c4a] transition-colors group"
              >
                <FaWhatsapp className="text-2xl text-text-1 group-hover:scale-110 transition-transform" />
                <div>
                  <div className="text-[10px] text-text-1/60 tracking-widest leading-none mb-1 text-left">
                    WhatsApp Support
                  </div>
                  <div className="text-sm font-semibold text-text-1">
                    {WhatsAppNo2}
                  </div>
                </div>
              </a>
            )}
          </div>
        </div>

        {CMSData?.["Home - Section 5 : We're Here to Help"] ? (
          <div
            className="flex-1 w-full space-y-3"
            dangerouslySetInnerHTML={{
              __html:
                CMSData?.["Home - Section 5 : We're Here to Help"]?.Template,
            }}
          />
        ) : (
          <div className="flex-1 w-full space-y-3">
            {[
              {
                title: "For Online Sellers",
                body: "Flipkart · Amazon · Meesho · Myntra sellers welcome. Bulk wholesale pricing for verified resellers.",
              },
              {
                title: "Payment Methods",
                body: "UPI QR Scanner (2 options) | Credit & Debit Card via Stripe. Cash on Delivery not available.",
              },
              {
                title: "Shipping",
                body: "Dispatched in 1–3 business days from Ahmedabad via DTDC, Nandan, Tirupati, Speed Post & Normal Post.",
              },
            ].map((info, i) => (
              <div
                key={i}
                className="bg-black/[0.02] border border-border-1 border-t border-t-gold-pale p-5 hover:border-t-gold-core transition-all"
              >
                <h4 className="text-[10px] font-bold tracking-[0.18em] uppercase text-gold-core mb-2.5">
                  {info.title}
                </h4>
                <p className="text-[13px] text-text-2 leading-relaxed font-light">
                  {info.body}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="gold-divider" />
      <Footer />
    </main>
  );
};

export default HomeClient;
